import { Controller } from "@hotwired/stimulus"

// Handles uploading a file to a document slot row (predefined or custom) and
// also submitting the "Add custom slot" modal form.
//
// This project uses Turbolinks (not Turbo) on admin pages, so we cannot rely
// on Turbo's automatic form interception or on Turbo.renderStreamMessage to
// apply the server's turbo_stream response. Instead, the controller:
//   1. Intercepts the form submit
//   2. Posts the FormData via fetch() with `Accept: text/vnd.turbo-stream.html`
//   3. Parses the response HTML for <turbo-stream> tags and applies the
//      replace / append / remove actions manually.
//
// Markup pattern:
//   <form data-controller="doc-upload"
//         data-doc-upload-row-id-value="row-dom-id"   (optional, for badge updates)
//         data-action="submit->doc-upload#submit">
//     <input type="file" onchange="this.form.requestSubmit()">
//   </form>
export default class extends Controller {
  static values = { rowId: String, confirm: String }

  submit(event) {
    if (event) event.preventDefault()

    const form = this.element
    if (!(form instanceof HTMLFormElement)) return

    // Optional confirmation prompt (used by the Delete action). Turbolinks
    // does not process data-turbo-confirm, so we guard here before fetching.
    if (this.hasConfirmValue && this.confirmValue && !window.confirm(this.confirmValue)) {
      return
    }

    this._setUploadingBadge()

    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const formData = new FormData(form)
    const method = ((form.method || "POST").toUpperCase() === "GET")
      ? "POST"
      : form.method.toUpperCase()

    fetch(form.action, {
      method,
      body: formData,
      headers: {
        "Accept": "text/vnd.turbo-stream.html, text/html;q=0.9, */*;q=0.5",
        "X-CSRF-Token": csrf || "",
        "X-Requested-With": "XMLHttpRequest"
      },
      credentials: "same-origin"
    })
      .then((response) => {
        const contentType = response.headers.get("content-type") || ""
        return response.text().then((body) => ({ ok: response.ok, status: response.status, contentType, body }))
      })
      .then(({ ok, status, contentType, body }) => {
        if (contentType.includes("text/vnd.turbo-stream.html")) {
          this._applyTurboStream(body)
          return
        }
        if (!ok) {
          this._setErrorBadge(body && body.length < 200 ? body : `Upload failed (${status})`)
          return
        }
        // Non-stream success (e.g. HTML redirect) — refresh the page so the
        // server-rendered state is reflected.
        window.location.reload()
      })
      .catch((err) => {
        this._setErrorBadge(err && err.message ? err.message : "Upload failed")
      })
  }

  _applyTurboStream(html) {
    const doc = new DOMParser().parseFromString(html, "text/html")
    const streams = doc.querySelectorAll("turbo-stream")
    streams.forEach((stream) => {
      const action = stream.getAttribute("action")
      const target = stream.getAttribute("target")
      const template = stream.querySelector("template")
      const fragment = template ? template.content.cloneNode(true) : null
      const targetEl = target ? document.getElementById(target) : null

      switch (action) {
        case "replace":
          if (targetEl && fragment) targetEl.replaceWith(fragment)
          break
        case "update":
          if (targetEl && fragment) {
            targetEl.innerHTML = ""
            targetEl.appendChild(fragment)
          }
          break
        case "append":
          if (targetEl && fragment) targetEl.appendChild(fragment)
          break
        case "prepend":
          if (targetEl && fragment) targetEl.insertBefore(fragment, targetEl.firstChild)
          break
        case "before":
          if (targetEl && fragment && targetEl.parentNode) targetEl.parentNode.insertBefore(fragment, targetEl)
          break
        case "after":
          if (targetEl && fragment && targetEl.parentNode) targetEl.parentNode.insertBefore(fragment, targetEl.nextSibling)
          break
        case "remove":
          if (targetEl) targetEl.remove()
          break
        default:
          // Unknown action — ignore
          break
      }
    })
  }

  _setUploadingBadge() {
    const badge = this._findBadge()
    if (!badge) return
    badge.className = "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"
    badge.textContent = "Uploading..."
  }

  _setErrorBadge(message) {
    const badge = this._findBadge()
    if (!badge) {
      window.alert(message || "Upload failed")
      return
    }
    badge.className = "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
    badge.textContent = "Failed"
    badge.title = message
  }

  _findBadge() {
    const row = this.rowIdValue ? document.getElementById(this.rowIdValue) : this.element.closest('[data-doc-row]')
    return row ? row.querySelector('[data-doc-status-badge]') : null
  }
}
